import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { Schemas } from '@npc/api-client'

import { Archive, ArrowLeft, LayoutGrid, List as ListIcon, Search, Sparkles, X } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'

import { CaptureBox, type CaptureBoxHandle } from './capture'
import { MemoItem, MemoTile, lockedPlaceholder, whenShort } from './item'
import { daysBetween } from '../../app/format'
import { MemoDetailDrawer } from './detail'
import { MoveMemoDialog } from './move'
import { ScopeLabel } from './scope'
import {
  useArchiveMemo,
  useArchivedMemos,
  useMemos,
  useProjects,
  useSearchMemos,
  useUnarchiveMemo,
  useUpdateMemo,
  type MemoScope,
} from '../../app/queries'
import { t } from '../../copy'
import { T } from '../../copy/text'
import { cn } from '../../components/cn'
import { Button } from '../../components/button'
import { buttonClass } from '../../components/button-style'
import { PageHeader } from '../../components/page'
import { Tag } from '../../components/tag'
import { QuickMemo } from '../../app/quick-memo'
import { createMemoDirect, uploadMemoAttachment } from '../../app/queries'
import {
  flushQueue,
  onQueueChange,
  queueAll,
  type QueuedMemo,
} from '../../app/memo-queue'

/**
 * 备忘录列表页 `MemoListPage`（设计稿 S01/S02/S03/S04/S06/S07，2026-08 交接包）。
 *
 * **同一个组件两个入口**：`/memos`（杂项）与 `/projects/:id/memos`
 * （项目内）。差别只在服务端绑不绑 project_id——数据、卡片、编辑器
 * 都是一套（设计稿「一个引擎，两个入口」）。
 *
 * ## 布局按设计稿
 *
 * 内容列**全宽流式**（16px 边距，无最大宽度）——设计稿的画布就是这样，
 * 上一版套了整站 1240px 的框，在宽屏上右侧空出一大条，就是「分辨率
 * 适配没做」的主要观感来源。页头 18px 标题（不是整站的 28px），滚动时
 * 粘在顶上；下面依次是输入框 → 找一条（搜索 + 带计数的筛选）→ 最近
 * （列表卡片）→ 作用域说明（notice 块）。
 *
 * ## 筛选与搜索都在本地
 *
 * chips 的计数来自当前数据（不是编的）；「最近改的」= 改动在七天内的。
 * 搜索当场筛正文与附件名——「语音转写」要等转写接上才有内容可搜，
 * 搜不到没有文字的语音条目，这是如实，不是漏。
 *
 * ## 旧的两个常驻区撤了
 *
 * 「最近一次 AI 读取」与「Lulu 建议记录」不在 S01 上——设计稿把读过的
 * 记录放在详情抽屉（S09），把候选放在对话里（S20）。列表页保持设计的
 * 干净，数据入口不丢。
 */
/** 手机断点判定的命令式版（按钮点击时用）：CSS 管 <700px，这里跟它同一条线。 */
function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 700px)').matches
}

type Chip =
  | { key: 'all' }
  | { key: 'recent' }
  | { key: 'voice' }
  | { key: 'files' }
  | { key: 'lulu' }
  | { key: 'ai'; access: 'shared' | 'hidden' }

const GLOBAL_CHIPS: Chip[] = [
  { key: 'all' },
  { key: 'recent' },
  { key: 'voice' },
  { key: 'files' },
  { key: 'ai', access: 'shared' },
  { key: 'ai', access: 'hidden' },
]

const PROJECT_CHIPS: Chip[] = [
  { key: 'all' },
  { key: 'ai', access: 'shared' },
  { key: 'ai', access: 'hidden' },
  { key: 'lulu' },
  { key: 'voice' },
]

export function MemoScreen() {
  return <MemoListPage scope={{}} />
}

export function ProjectMemoScreen(props: { projectId: string }) {
  const projects = useProjects()
  const name =
    projects.data?.items.find((p) => p.id === props.projectId)?.name ?? props.projectId
  return <MemoListPage scope={{ projectId: props.projectId }} projectName={name} />
}

function MemoListPage(props: { scope: MemoScope; projectName?: string }) {
  const { scope } = props
  const inProject = !!scope.projectId
  // 数据一次取全（access 筛选在本地做）：chips 的计数要对着整份来，
  // 切档走服务端会数不出来。
  const memos = useMemos(scope)
  // 项目名：跨搜分组组头要用（杂项页也搜得到项目条目）
  const projects = useProjects()
  const [openId, setOpenId] = useState<string | null>(null)
  const [moving, setMoving] = useState<Schemas['Memo'] | null>(null)
  const [query, setQuery] = useState('')
  // 手机端搜索折叠态（M01：导航里那个放大镜是全局搜索，本页搜索收成行内图标）
  const [searchOpen, setSearchOpen] = useState(false)
  // 跨项目搜索（R1-1）：明确的动作，默认关，状态不持久化——
  // 下次进来还是只搜当前作用域，不默默把项目内容混进来
  const [crossSearch, setCrossSearch] = useState(false)
  // 离线队列（R2-4）：断网写的条目带「等上传」置顶显示，online 自动补传
  const [queued, setQueued] = useState<QueuedMemo[]>([])
  /** 死单（巡检修复）：在线仍补不上的（项目没了等），单独列出、可移除，不卡后面的 */
  const [deadItems, setDeadItems] = useState<QueuedMemo[]>([])
  const queryClient = useQueryClient()
  const flushNow = useCallback(async () => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return
    const { dead } = await flushQueue(
      async (item) => {
        const created = await createMemoDirect({ body: item.body, projectId: item.projectId })
        for (const file of item.files) {
          await uploadMemoAttachment({ memoId: created.id, file })
        }
      },
      (item) => setDeadItems((prev) => [...prev, item]),
    )
    if (dead.length === 0) setDeadItems([])
    setQueued(await queueAll())
    void queryClient.invalidateQueries({ queryKey: ['memos'] })
  }, [queryClient])
  useEffect(() => {
    void queueAll().then(setQueued)
    const off = onQueueChange(() => {
      void queueAll().then(setQueued)
    })
    const online = () => void flushNow()
    window.addEventListener('online', online)
    // 挂载时如果已经在线且队列非空，也补传一次（上次断网残留）
    void flushNow()
    return () => {
      off()
      window.removeEventListener('online', online)
    }
  }, [flushNow])

  // 撤销 toast（R1-4）：归档/移动后 5 秒内可撤
  const [undoToast, setUndoToast] = useState<{ text: string; undo: () => void } | null>(null)
  const undoTimer = useRef<number | null>(null)
  function showUndo(text: string, undo: () => void) {
    if (undoTimer.current !== null) window.clearTimeout(undoTimer.current)
    setUndoToast({ text, undo })
    undoTimer.current = window.setTimeout(() => setUndoToast(null), 5000)
  }
  useEffect(() => () => {
    if (undoTimer.current !== null) window.clearTimeout(undoTimer.current)
  }, [])
  const [chip, setChip] = useState<Chip>({ key: 'all' })
  // 显示方式：单列列表是设计稿的默认；平铺（记事本式卡片墙）是用户
  // 自己要的第二种，选择记在本机，下次进来还是上次的那个。
  const [view, setView] = useState<'list' | 'tiles'>(() => {
    try {
      return window.localStorage.getItem('memo-view') === 'tiles' ? 'tiles' : 'list'
    } catch {
      return 'list'
    }
  })
  function switchView(next: 'list' | 'tiles') {
    setView(next)
    try {
      window.localStorage.setItem('memo-view', next)
    } catch {
      // localStorage 不可用就只记住这一屏的
    }
  }
  const archive = useArchiveMemo()
  const unarchive = useUnarchiveMemo()
  const update = useUpdateMemo()
  const captureRef = useRef<CaptureBoxHandle>(null)
  // 手机端底部输入栏拉起的快记浮层（桌面的 N 键浮层是同一个组件）
  const [quickOpen, setQuickOpen] = useState(false)

  const crossActive = crossSearch && query.trim() !== ''
  const search = useSearchMemos(query.trim(), crossActive)
  const searchItems = search.data?.items ?? []
  const items = useMemo(() => {
    if (crossActive) return searchItems
    const all = memos.data?.items ?? []
    return all.filter((memo) => {
      if (!matchChip(memo, chip)) return false
      const q = query.trim().toLowerCase()
      if (!q) return true
      if ((memo.body ?? '').toLowerCase().includes(q)) return true
      return (memo.attachments ?? []).some((a) => a.name.toLowerCase().includes(q))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memos.data, chip, query, crossActive, search.data])

  const counts = useMemo(() => {
    const all = memos.data?.items ?? []
    const count = (target: Chip) => all.filter((m) => matchChip(m, target)).length
    return { total: all.length, byChip: count }
  }, [memos.data])

  const open =
    (memos.data?.items ?? []).find((m) => m.id === openId) ??
    (crossActive ? searchItems.find((m) => m.id === openId) : undefined) ??
    null

  return (
    /* 整页主题面：memo 模块用自己的深/浅色板（themes.css）。
       ≤1440 与设计稿画布同宽（全宽流式，16px 边距）；再往上**限宽居中**——
       设计稿只画到 1440，超宽屏把正文行拉到 2000px+ 不是适配是拉伸。 */
    <div className="flex flex-1 flex-col bg-m-bg0 text-m-t1" data-testid="memo-page-header">
      {/* 页头与其它 tab 用同一份 PageHeader（2026-09-11 用户裁决：备忘录顶部
          与别的 tab 差异太大，样式与字号统一）。原来那条 sticky 全幅贴边条
          和它的自定字号（26/15/12.5px）是设计稿 S01 的旧排法，撤掉；
          面包屑、按钮、徽章全部换成全站同一套 npc 档位。 */}
      <div className="px-[16px] pt-npc-page-top max-[700px]:px-npc-grid-sm">
        <div className="mx-auto w-full max-w-[1440px]">
        {inProject ? (
          <PageHeader
            className="mb-npc-card-lg"
            breadcrumb={
              <>
                <span>{t('memo.crumb.project')}</span>
                <span className="mx-[6px]">/</span>
                <span className="max-w-[220px] truncate">{props.projectName}</span>
                <span className="mx-[6px]">/</span>
                <span>{t('nav.ptab.memo')}</span>
              </>
            }
            title={
              <>
                <span className="truncate">
                  {props.projectName}
                  <span className="mx-[6px] font-normal text-npc-ink-softer">/</span>
                  <T id="nav.ptab.memo" />
                </span>
                <Tag data-testid="memo-scope-badge" className="ml-[9px] align-middle">
                  <T id="memo.page.badge.project" />
                </Tag>
              </>
            }
            subtitle={<T id="memo.page.subtitle.project" />}
            actions={
              <>
                <Button
                  variant="primary"
                  onClick={() => {
                    // 手机端内联输入框是藏着的——点它去开底部快记浮层（M01）
                    if (isMobileViewport()) setQuickOpen(true)
                    else captureRef.current?.focus()
                  }}
                  data-testid="memo-write"
                >
                  <T id="memo.page.write" />
                </Button>
                <Link
                  to="/memos/archived"
                  data-testid="memo-archive-link"
                  className={buttonClass({ variant: 'secondary' })}
                >
                  <Archive size={13} strokeWidth={1.8} aria-hidden />
                  <T id="memo.archive.entry" />
                </Link>
                {/* Lulu 建议记录（2026-09-09 用户定的）：单独一个页签放
                    （/memos/lulu），这里只留入口 */}
                <Link
                  to="/memos/lulu"
                  data-testid="memo-lulu-link"
                  className={buttonClass({ variant: 'secondary' })}
                >
                  <Sparkles size={13} strokeWidth={1.8} aria-hidden />
                  <T id="memo.candidates.heading" />
                </Link>
              </>
            }
          />
        ) : (
          <>
            {/* 杂项页（用户定稿）：不显示标题与副题——顶部导航已经标了在哪儿，
                再来一行「杂项备忘录 + 还没归到任何项目的东西都在这儿…」是重复占位。
                h1 留给读屏（sr-only），项目页保留面包屑与标题（那是载重信息：
                得知道自己在哪个项目里）。 */}
            <h1 className="sr-only m-0">
              <T id="memo.page.title" />
            </h1>
            <div className="mb-npc-card-lg flex justify-end gap-npc-gap-sm">
              <Button
                variant="primary"
                onClick={() => {
                  // 手机端内联输入框是藏着的——点它去开底部快记浮层（M01）
                  if (isMobileViewport()) setQuickOpen(true)
                  else captureRef.current?.focus()
                }}
                data-testid="memo-write"
              >
                <T id="memo.page.write" />
              </Button>
              <Link
                to="/memos/archived"
                data-testid="memo-archive-link"
                className={buttonClass({ variant: 'secondary' })}
              >
                <Archive size={13} strokeWidth={1.8} aria-hidden />
                <T id="memo.archive.entry" />
              </Link>
              {/* Lulu 建议记录（2026-09-09 用户定的）：不长在这页顶上，
                  单独一个页签放（/memos/lulu），这里只留入口 */}
              <Link
                to="/memos/lulu"
                data-testid="memo-lulu-link"
                className={buttonClass({ variant: 'secondary' })}
              >
                <Sparkles size={13} strokeWidth={1.8} aria-hidden />
                <T id="memo.candidates.heading" />
              </Link>
            </div>
          </>
        )}
        </div>
      </div>

      <div className={cn('px-[16px] pb-[48px]', 'max-[700px]:px-npc-grid-sm max-[700px]:pb-[68px]')}>
        <div className="mx-auto w-full max-w-[1440px]">
        {/* 手机端（M01）没有内联输入框——底部固定输入栏代替（页面最底下），
            桌面端照设计稿 S01 常开在顶部 */}
        <div className="mb-[16px] max-[700px]:hidden">
          <CaptureBox
            ref={captureRef}
            scope={{ ...scope, projectName: props.projectName }}
            placeholder={
              inProject ? t('memo.empty.project.placeholder') : t('memo.empty.global.placeholder')
            }
          />
        </div>

        {/* 加载（S03）：输入框上面那条备注已经说了「现在也能写」，这里不再挡屏 */}
        {memos.isPending ? (
          <p className="m-0 text-[12px] text-m-t3" data-testid="memo-list-loading"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
            <T id="memo.loading" />
            <span className="mx-[6px]">·</span>
            <T id="memo.loading.note" />
          </p>
        ) : memos.error ? (
          <div
            role="alert"
            data-testid="memo-list-error"
            className="flex items-start gap-[11px] rounded-npc-card border border-m-line2 bg-m-bg1 px-[14px] py-[12px]"
          >
            <span className="mt-[6px] h-[7px] w-[7px] shrink-0 rounded-full bg-m-bad" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block text-[17.5px] font-medium text-m-t1"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                <T id="memo.error.title" />
              </span>
              <span className="mt-[2px] block text-[11.5px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                <T id="memo.error.body" />
              </span>
              <button
                type="button"
                onClick={() => void memos.refetch()}
                className="mt-[9px] h-[30px] cursor-pointer rounded-[7px] border-none bg-m-pri px-[14px] font-[inherit] text-[12.5px] font-medium text-m-pri-t"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
              >
                <T id="memo.error.retry" />
              </button>
            </span>
          </div>
        ) : (memos.data?.items ?? []).length === 0 ? (
          /* 空态（S02/S07）：讲这一页放什么，不写「暂无数据」 */
          <div
            data-testid="memo-list-empty"
            className="rounded-npc-card border border-dashed border-m-line2 bg-m-bg1 px-[18px] py-[26px] text-center"
          >
            <h2 className="m-0 text-[19px] font-medium text-m-t1"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              <T id={inProject ? 'memo.empty.project.title' : 'memo.empty.global.title'} />
            </h2>
            <p className="mx-auto mb-[13px] mt-[6px] max-w-[520px] text-[15px] leading-[1.6] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              <T id={inProject ? 'memo.empty.project.body' : 'memo.empty.global.body'} />
            </p>
            <div className="flex flex-wrap justify-center gap-[8px]">
              <button
                type="button"
                onClick={() => {
                // 手机端内联输入框是藏着的——点它去开底部快记浮层（M01）
                if (isMobileViewport()) setQuickOpen(true)
                else captureRef.current?.focus()
              }}
                className="h-[32px] cursor-pointer rounded-[7px] border-none bg-m-pri px-[14px] font-[inherit] text-[12.5px] font-medium text-m-pri-t"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
              >
                <T id="memo.page.write" />
              </button>
              <button
                type="button"
                onClick={() => {
                  if (isMobileViewport()) setQuickOpen(true)
                  else captureRef.current?.openVoice()
                }}
                data-testid="memo-empty-voice"
                className="h-[32px] cursor-pointer rounded-[7px] border border-m-line2 bg-m-bg1 px-[14px] font-[inherit] text-[12.5px] text-m-t2"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
              >
                <T id="memo.voice.record" />
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* 离线队列（R2-4，S05）：断网写的还在本机——置顶、带「等上传」，
                网络回来自动补传后从这里消失。点不开：服务器上还没有这条。 */}
            {queued.length > 0 ? (
              <div className="mb-[16px]" data-testid="memo-queue">
                <BlockHead label={t('memo.list.uploads.waiting')} meta={`${queued.length} 条`} />
                <ul className="m-0 list-none overflow-hidden rounded-npc-card border border-m-line2 bg-m-bg1 p-0">
                  {queued.map((item) => (
                    <li
                      key={item.id}
                      data-testid="memo-queue-item"
                      className="flex items-start gap-[11px] border-b border-m-line1 px-[13px] py-[12px] last:border-b-0"
                    >
                      <ScopeLabel
                        kind={
                          item.projectId
                            ? {
                                project:
                                  projects.data?.items.find((p) => p.id === item.projectId)?.name ??
                                  t('memo.crumb.project'),
                              }
                            : 'misc'
                        }
                      />
                      <div className="min-w-0 flex-1">
                        <p className="m-0 line-clamp-2 text-[18px] leading-[1.7] text-m-t1"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                          {item.body}
                        </p>
                        <p className="m-0 mt-[7px] flex flex-wrap items-center gap-x-[9px] gap-y-[3px] text-[14px] text-m-wn-t"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                          {t('memo.list.uploads.waiting')}
                          {item.files.length > 0 ? <span>· {item.files.length} 个附件</span> : null}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {/* 死单（巡检修复）：在线仍补不上的，如实列出原因并可移除 */}
            {deadItems.length > 0 ? (
              <div className="mb-[16px]" data-testid="memo-queue-dead">
                <ul className="m-0 list-none overflow-hidden rounded-npc-card border border-m-bad-t/35 bg-m-bad-s p-0">
                  {deadItems.map((item) => (
                    <li
                      key={item.id}
                      data-testid="memo-queue-dead-item"
                      className="flex items-start gap-[11px] px-[13px] py-[12px]"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="m-0 line-clamp-2 text-[16px] leading-[1.65] text-m-t1"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                          {item.body}
                        </p>
                        <p className="m-0 mt-[6px] text-[13.5px] text-m-bad-t"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                          <T id="memo.queue.dead" />
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setDeadItems((prev) => prev.filter((d) => d.id !== item.id))}
                        data-testid="memo-queue-dead-remove"
                        className="h-[30px] shrink-0 cursor-pointer rounded-[7px] border border-m-line2 bg-m-bg1 px-[11px] font-[inherit] text-[13px] text-m-t2 hover:text-m-t1"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                      >
                        <T id="memo.queue.dead.remove" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {/* 找一条（S01）：搜索 + 带计数的筛选。计数对着整份数据数，
                不是对着当前筛选结果数——那是循环论证。 */}
            <BlockHead label={t('memo.list.head.find')} />
            <div className="mb-[16px] flex flex-wrap items-center gap-[8px]" role="group" aria-label={t('memo.list.head.find')}>
              <div
                data-testid="memo-search-box"
                className={cn(
                  'flex h-[38px] min-w-[220px] flex-1 items-center gap-[7px] rounded-[8px] border border-m-line2 bg-m-bg1 px-[10px] sm:max-w-[320px]',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                  // 手机端（M01）：搜索收成图标按钮，点开才占一行
                  !searchOpen && query === '' && 'max-[700px]:w-[38px] max-[700px]:min-w-0 max-[700px]:flex-none max-[700px]:px-0',
                  (searchOpen || query !== '') && 'max-[700px]:w-full max-[700px]:sm:max-w-none',
                )}
              >
                {searchOpen || query !== '' ? null : (
                  <button
                    type="button"
                    aria-label={t('memo.mobile.search.open')}
                    data-testid="memo-search-open"
                    onClick={() => setSearchOpen(true)}
                    className="mx-auto hidden cursor-pointer border-none bg-transparent p-0 text-m-t3 max-[700px]:block"
                  >
                    <Search size={14} strokeWidth={2} aria-hidden />
                  </button>
                )}
                {/* 桌面常驻输入框；手机折叠态隐藏这一组（外面的图标按钮替代） */}
                <span className={cn('flex min-w-0 flex-1 items-center gap-[7px]', !searchOpen && query === '' && 'max-[700px]:hidden')}>
                <Search size={13} strokeWidth={2} className="shrink-0 text-m-t4" aria-hidden />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t('memo.list.search')}
                  aria-label={t('memo.list.search')}
                  data-testid="memo-search"
                  className="min-w-0 flex-1 border-none bg-transparent p-0 font-[inherit] text-[15px] text-m-t1 outline-none placeholder:text-m-t4"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                />
                {query ? (
                  <button
                    type="button"
                    aria-label={t('ui.close')}
                    onClick={() => setQuery('')}
                    className="shrink-0 cursor-pointer border-none bg-transparent p-0 text-m-t4 hover:text-m-t2"
                    data-testid="memo-search-clear"
                  >
                    <X size={12} aria-hidden />
                  </button>
                ) : null}
                {searchOpen ? (
                  <button
                    type="button"
                    aria-label={t('ui.close')}
                    onClick={() => { setSearchOpen(false); setQuery('') }}
                    className="shrink-0 cursor-pointer border-none bg-transparent p-0 text-m-t4 hover:text-m-t2"
                    data-testid="memo-search-close"
                  >
                    <X size={12} aria-hidden />
                  </button>
                ) : null}
                </span>
              </div>
              {/* 跨搜时 chips 不参与结果（结果来自搜索端点）——藏起来，不给无效操作 */}
              {!crossActive ? (inProject ? PROJECT_CHIPS : GLOBAL_CHIPS).map((option) => {
                const active = chipKey(chip) === chipKey(option)
                const count = counts.byChip(option)
                return (
                  <button
                    key={chipKey(option)}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setChip(option)}
                    className={cn(
                      'flex h-[38px] cursor-pointer items-center gap-[6px] rounded-[8px] border px-[13px] font-[inherit] text-[15px] whitespace-nowrap',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                      active
                        ? 'border-m-line3 bg-m-bg3 font-medium text-m-t1'
                        : 'border-m-line2 bg-m-bg1 text-m-t3 hover:text-m-t2',
                    )}
                    data-testid={`memo-filter-${chipKey(option)}`}
                  >
                    <span>{chipLabel(option)}</span>
                    <span className="font-npc-mono text-[13.5px] text-m-t4">{count}</span> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                  </button>
                )
              }) : null}

              {/* 跨项目搜索（R1-1）：明确动作，默认关、不持久化 */}
              <button
                type="button"
                aria-pressed={crossSearch}
                onClick={() => setCrossSearch((v) => !v)}
                data-testid="memo-search-cross"
                className={cn(
                  'flex h-[38px] shrink-0 cursor-pointer items-center gap-[6px] rounded-[8px] border px-[13px] font-[inherit] text-[15px] whitespace-nowrap',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                  crossSearch
                    ? 'border-m-line3 bg-m-bg3 font-medium text-m-t1'
                    : 'border-m-line2 bg-m-bg1 text-m-t3 hover:text-m-t2',
                )}
              >
                <T id="memo.search.cross" />
              </button>

              {/* 显示方式（用户要的平铺）：列表是设计稿默认，平铺是记事本式卡片墙 */}
              <span className="ml-auto flex shrink-0 items-center gap-[4px]" role="group" aria-label={t('memo.view.label')}>
                {(
                  [
                    { key: 'list' as const, Icon: ListIcon, label: t('memo.view.list') },
                    { key: 'tiles' as const, Icon: LayoutGrid, label: t('memo.view.tiles') },
                  ]
                ).map(({ key, Icon, label }) => (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={view === key}
                    title={label}
                    aria-label={label}
                    onClick={() => switchView(key)}
                    data-testid={`memo-view-${key}`}
                    className={cn(
                      'flex h-[38px] w-[38px] cursor-pointer items-center justify-center rounded-[8px] border',  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                      view === key
                        ? 'border-m-line3 bg-m-bg3 text-m-t1'
                        : 'border-m-line2 bg-m-bg1 text-m-t3 hover:text-m-t2',
                    )}
                  >
                    <Icon size={13} strokeWidth={1.9} aria-hidden />
                  </button>
                ))}
              </span>
            </div>

            {/* 最近（S01）：一个卡片容器装所有条目，条目间 --l1 分隔 */}
            <BlockHead
              label={inProject ? t('memo.list.this.project') : t('memo.list.recent')}
              meta={t('memo.list.recent.meta', { count: String(counts.total) })}
            />
            {crossActive ? (
              search.isPending ? (
                <p className="m-0 text-[15px] text-m-t3" data-testid="memo-search-loading"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                  <T id="memo.loading" />
                </p>
              ) : items.length === 0 ? (
                <div className="rounded-npc-card border border-dashed border-m-line2 bg-m-bg1 px-[18px] py-[22px] text-center" data-testid="memo-search-empty">
                  <p className="m-0 text-[15px] text-m-t2"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                    <T id="memo.list.search.empty" />
                  </p>
                </div>
              ) : (
                /* 跨搜结果按作用域分组：杂项一组、每个项目一组，组头写明来自哪 */
                (() => {
                  const groups = groupByScope(items, projects.data?.items ?? [])
                  return groups.map((g, i) => (
                    <div key={g.key} className="mb-[4px]">
                      <BlockHead label={g.label} meta={g.count} />
                      <MemoRows
                        items={g.items}
                        view={view}
                        query={query.trim()}
                        openId={openId}
                        projectName={props.projectName}
                        projects={projects.data?.items ?? []}
                        onOpen={setOpenId}
                        testId={i === 0 ? view : undefined}
                      />
                    </div>
                  ))
                })()
              )
            ) : items.length === 0 ? (
              <div
                className="rounded-npc-card border border-dashed border-m-line2 bg-m-bg1 px-[18px] py-[22px] text-center"
                data-testid="memo-list-filtered-empty"
              >
                <p className="m-0 text-[12.5px] text-m-t2"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                  <T id="memo.list.search.empty" />
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setQuery('')
                    setChip({ key: 'all' })
                  }}
                  className="mx-auto mt-[10px] block h-[30px] cursor-pointer rounded-[7px] border border-m-line2 bg-m-bg1 px-[14px] font-[inherit] text-[12.5px] text-m-t2"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                >
                  <T id="memo.list.search.clear" />
                </button>
              </div>
            ) : view === 'tiles' || isMobileViewport() ? (
              /* 平铺/手机：不分组（卡片墙本身无序感，手机一列到底） */
              <MemoRows
                items={items}
                view={view === 'tiles' || isMobileViewport() ? 'tiles' : 'list'}
                query={query.trim()}
                openId={openId}
                projectName={props.projectName}
                projects={projects.data?.items ?? []}
                onOpen={setOpenId}
                testId={view === 'tiles' || isMobileViewport() ? 'tiles' : 'list'}
              />
            ) : (
              /* 列表按时间分组（R1-5）：今天/昨天/本周/更早——扫读的时间锚点 */
              (() => {
                const groups = groupByTime(items)
                return (
                  <>
                    {groups.map((g, i) => (
                      <div key={g.key} className="mb-[4px]">
                        <BlockHead label={g.label} meta={g.count} />
                        <MemoRows
                          items={g.items}
                          view="list"
                          query={query.trim()}
                          openId={openId}
                          projectName={props.projectName}
                          projects={projects.data?.items ?? []}
                          onOpen={setOpenId}
                          testId={i === 0 ? 'list' : undefined}
                        />
                      </div>
                    ))}
                  </>
                )
              })()
            )}
          </>
        )}

        {/* 作用域边界告知（S01/S06 各一句）：notice 块，7px 圆点开头 */}
        <div
          className="mt-[16px] flex items-start gap-[11px] rounded-npc-card border border-m-line2 bg-m-bg1 px-[14px] py-[12px]"
          data-testid="memo-scope-notice"
        >
          <span className="mt-[6px] h-[7px] w-[7px] shrink-0 rounded-full bg-m-t4" aria-hidden />
          <p className="m-0 min-w-0">
            <span className="block text-[17.5px] font-medium text-m-t1"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              <T id={inProject ? 'memo.page.notice.project' : 'memo.page.notice.global'} />
            </span>
            <span className="mt-[3px] block text-[14.5px] leading-[1.6] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              <T
                id={
                  inProject ? 'memo.page.notice.project.body' : 'memo.page.notice.global.body'
                }
              />
            </span>
          </p>
        </div>
        </div>
      </div>

      {/* 撤销 toast（R1-4）：5 秒内可撤，归档原地恢复、移动移回并还原 AI 权限 */}
      {undoToast ? (
        <div
          data-testid="memo-undo-toast"
          className="fixed bottom-[24px] left-1/2 z-40 flex -translate-x-1/2 items-center gap-[12px] rounded-[8px] border border-m-line2 bg-m-bg1 px-[14px] py-[10px] shadow-[var(--m-pop)] max-[700px]:bottom-[64px]"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
          role="status"
        >
          <span className="text-[14px] text-m-t2">{undoToast.text}</span> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
          <button
            type="button"
            onClick={() => {
              undoToast.undo()
              setUndoToast(null)
              if (undoTimer.current !== null) window.clearTimeout(undoTimer.current)
            }}
            data-testid="memo-undo-action"
            className="h-[30px] cursor-pointer rounded-[7px] border-none bg-m-pri px-[12px] font-[inherit] text-[13px] font-medium text-m-pri-t"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
          >
            <T id="memo.undo.action" />
          </button>
        </div>
      ) : null}

      {/* 手机端底部固定输入栏（M01「写点什么…」）：点它弹快记浮层，
          不是直接输入——键盘顶上来时作用域行还在浮层里看得见（M08） */}
      <button
        type="button"
        onClick={() => setQuickOpen(true)}
        data-testid="memo-mobile-input"
        className="fixed inset-x-0 bottom-0 z-30 hidden h-[52px] items-center gap-[9px] border-t border-m-line2 bg-m-bg1 px-[14px] text-left font-[inherit] max-[700px]:flex"
      >
        <span className="flex-1 text-[17px] text-m-t4"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
          {t('memo.mobile.input')}
        </span>
      </button>

      {quickOpen ? (
        <QuickMemo open onClose={() => setQuickOpen(false)} />
      ) : null}

      {open ? (
        <MemoDetailDrawer
          memo={open}
          projectName={props.projectName ?? ''}
          onClose={() => setOpenId(null)}
          onMove={(m) => setMoving(m)}
          onArchive={(m) => {
            archive.mutate(m.id, {
              onSuccess: () => showUndo(t('memo.undo.archived'), () => unarchive.mutate(m.id)),
            })
            setOpenId(null)
          }}
        />
      ) : null}
      {moving ? (
        <MoveMemoDialog
          memo={moving}
          onClose={() => setMoving(null)}
          onMoved={() => {
            // 撤销要原作用域与原 AI 权限：移动会重置权限，撤销时一并还原
            const from = moving.project_id ?? null
            const access = moving.ai_access
            showUndo(t('memo.undo.moved'), () => {
              void update.mutateAsync({ memoId: moving.id, projectId: from, aiAccess: access })
            })
            setOpenId(null)
          }}
        />
      ) : null}
    </div>
  )
}

/** 匹配一个 chip 的谓词。「最近改的」= 七天内动过的。 */
function matchChip(memo: Schemas['Memo'], chip: Chip): boolean {
  switch (chip.key) {
    case 'all':
      return true
    case 'recent':
      return recentWithin(memo, 7)
    case 'voice':
      return !!memo.audio
    case 'files':
      return (memo.attachments ?? []).length > 0
    case 'lulu':
      return memo.author === 'ai'
    case 'ai':
      return memo.ai_access === chip.access
  }
}

function recentWithin(memo: Schemas['Memo'], days: number): boolean {
  const then = new Date(memo.updated_at ?? memo.created_at).getTime()
  return Date.now() - then <= days * 86400_000
}

function chipKey(chip: Chip): string {
  return chip.key === 'ai' ? `ai-${chip.access}` : chip.key
}

function chipLabel(chip: Chip): string {
  switch (chip.key) {
    case 'all':
      return t('memo.list.filter.all')
    case 'recent':
      return t('memo.list.filter.recent')
    case 'voice':
      return t('memo.list.filter.voice')
    case 'files':
      return t('memo.list.filter.files')
    case 'lulu':
      return t('memo.list.filter.lulu')
    case 'ai':
      return chip.access === 'shared' ? t('memo.ai.shared') : t('memo.ai.hidden')
  }
}

/** 一组条目的容器：列表共享卡片容器，平铺是网格（两视图同一份数据）。 */
function MemoRows(props: {
  items: Schemas['Memo'][]
  view: 'list' | 'tiles'
  query?: string
  openId: string | null
  projectName?: string
  /** 项目表：跨搜结果来自别的项目时，徽标要查真名，不能用当前页的项目名 */
  projects?: { id: string; name: string }[]
  onOpen: (id: string) => void
  /** 分组渲染时只有第一组挂 memo-list/memo-tiles testid——断言要求全页恰好一个 */
  testId?: 'list' | 'tiles'
}) {
  const scopeName = (memo: Schemas['Memo']) =>
    memo.project_id
      ? (props.projects?.find((p) => p.id === memo.project_id)?.name ??
        props.projectName ??
        t('memo.crumb.project'))
      : t('memo.scope.misc')
  if (props.view === 'tiles') {
    return (
      <ul
        className="m-0 grid list-none gap-[8px] p-0 grid-cols-[repeat(auto-fill,minmax(min(45%,260px),1fr))]"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
        data-testid={props.testId === 'tiles' ? 'memo-tiles' : undefined}
      >
        {props.items.map((memo) => (
          <MemoTile
            key={memo.id}
            memo={memo}
            scopeName={scopeName(memo)}
            query={props.query}
            selected={memo.id === props.openId}
            onOpen={props.onOpen}
          />
        ))}
      </ul>
    )
  }
  return (
    <ul
      className={cn(
        'm-0 list-none overflow-hidden rounded-npc-card border border-m-line2 bg-m-bg1 p-0',
        'max-[700px]:flex max-[700px]:list-none max-[700px]:flex-col max-[700px]:gap-[8px] max-[700px]:rounded-none max-[700px]:border-0 max-[700px]:bg-transparent max-[700px]:p-0',
      )}
      data-testid={props.testId === 'list' ? 'memo-list' : undefined}
    >
      {props.items.map((memo) => (
        <MemoItem
          key={memo.id}
          memo={memo}
          scopeName={scopeName(memo)}
          query={props.query}
          selected={memo.id === props.openId}
          onOpen={props.onOpen}
        />
      ))}
    </ul>
  )
}

type ItemGroup = { key: string; label: string; count: string; items: Schemas['Memo'][] }

/** 时间分组（R1-5）：今天/昨天/本周/更早。items 已按最近改动降序。 */
function groupByTime(items: Schemas['Memo'][]): ItemGroup[] {
  const groups: Array<{ key: string; label: string; maxDays: number }> = [
    { key: 'today', label: t('memo.group.today'), maxDays: 0 },
    { key: 'yesterday', label: t('memo.group.yesterday'), maxDays: 1 },
    { key: 'week', label: t('memo.group.week'), maxDays: 7 },
    { key: 'earlier', label: t('memo.group.earlier'), maxDays: Number.MAX_SAFE_INTEGER },
  ]
  const out: ItemGroup[] = []
  let gi = 0
  for (const memo of items) {
    const days = daysBetween(memo.updated_at ?? memo.created_at ?? new Date(0).toISOString())
    while (gi < groups.length - 1 && days > groups[gi]!.maxDays) gi += 1
    const g = groups[gi]!
    const last = out[out.length - 1]
    if (last && last.key === g.key) {
      last.items.push(memo)
      last.count = `${last.items.length} 条`
    } else {
      out.push({ key: g.key, label: g.label, count: '1 条', items: [memo] })
    }
  }
  return out
}

/** 跨搜结果按作用域分组（R1-1）：杂项一组，每个项目一组。 */
function groupByScope(items: Schemas['Memo'][], projects: { id: string; name: string }[]): ItemGroup[] {
  const out: ItemGroup[] = []
  const labelOf = (memo: Schemas['Memo']) =>
    memo.project_id
      ? (projects.find((p) => p.id === memo.project_id)?.name ?? t('memo.crumb.project'))
      : t('memo.scope.misc')
  for (const memo of items) {
    const label = labelOf(memo)
    const key = String(memo.project_id ?? 'misc')
    const last = out[out.length - 1]
    if (last && last.key === key) {
      last.items.push(memo)
      last.count = `${last.items.length} 条`
    } else {
      out.push({ key, label, count: '1 条', items: [memo] })
    }
  }
  return out
}

/** 区块小标题（设计稿）：11px/700/字距 .05em + 1px 通长线 + 右侧 meta。 */
function BlockHead(props: { label: string; meta?: string }) {
  return (
    <div className="mb-[9px] mt-[4px] flex items-center gap-[9px]">
      <span className="shrink-0 whitespace-nowrap font-npc-display text-[14px] font-bold tracking-[0.05em] text-m-t4"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
        {props.label}
      </span>
      <span className="h-px flex-1 bg-m-line1" aria-hidden />
      {props.meta ? (
        <span className="shrink-0 whitespace-nowrap font-npc-mono text-[10.5px] text-m-t4"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
          {props.meta}
        </span>
      ) : null}
    </div>
  )
}

/**
 * 归档区（S18）：杂项和项目的归档混排，每条带作用域标签。
 * 归档不是删除：内容还在，AI 权限保持原样，一键拿回来。
 */
export function MemoArchiveScreen() {
  const archived = useArchivedMemos()
  const restore = useUnarchiveMemo()
  const projects = useProjects()
  // 归档区可搜（R1-3）：当场筛正文与附件名
  const [query, setQuery] = useState('')
  const q = query.trim().toLowerCase()
  const items = (archived.data?.items ?? []).filter(
    (memo) =>
      !q ||
      (memo.body ?? '').toLowerCase().includes(q) ||
      (memo.attachments ?? []).some((a) => a.name.toLowerCase().includes(q)),
  )

  return (
    <div className="flex flex-1 flex-col bg-m-bg0 text-m-t1">
      {/* 与列表页同一次统一（2026-09-11）：页头换全站同一份 PageHeader，
          撤掉 sticky 全幅条与自定字号。 */}
      <div className="px-[16px] pt-npc-page-top max-[700px]:px-npc-grid-sm">
        <div className="mx-auto w-full max-w-[1440px]">
          <PageHeader
            className="mb-npc-card-lg"
            title={<T id="memo.archive.title" />}
            subtitle={<T id="memo.archive.subtitle" />}
            actions={
              <Link
                to="/memos"
                data-testid="memo-archive-back"
                className={buttonClass({ variant: 'secondary' })}
              >
                <ArrowLeft size={13} strokeWidth={1.8} aria-hidden />
                <T id="memo.page.back.list" />
              </Link>
            }
          />
        </div>
      </div>

      <div className="px-[16px] pb-[48px] max-[700px]:px-npc-grid-sm">
        <div className="mx-auto w-full max-w-[1440px]">
        <div
          className="mb-[16px] flex items-start gap-[11px] rounded-npc-card border border-m-line2 bg-m-bg1 px-[14px] py-[12px]"
          data-testid="memo-archive-notice"
        >
          <span className="mt-[6px] h-[7px] w-[7px] shrink-0 rounded-full bg-m-t4" aria-hidden />
          <p className="m-0 min-w-0">
            <span className="block text-[17.5px] font-medium text-m-t1"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              <T id="memo.archive.notice" />
            </span>
            <span className="mt-[3px] block text-[14.5px] leading-[1.6] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              <T id="memo.archive.notice.body" />
            </span>
          </p>
        </div>

        <div className="mb-[12px] flex h-[38px] items-center gap-[7px] rounded-[8px] border border-m-line2 bg-m-bg1 px-[11px]"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
          <Search size={14} strokeWidth={2} className="shrink-0 text-m-t4" aria-hidden />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('memo.list.search')}
            aria-label={t('memo.list.search')}
            data-testid="memo-archive-search"
            className="min-w-0 flex-1 border-none bg-transparent p-0 font-[inherit] text-[15px] text-m-t1 outline-none placeholder:text-m-t4"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
          />
          {query ? (
            <button
              type="button"
              aria-label={t('ui.close')}
              onClick={() => setQuery('')}
              className="shrink-0 cursor-pointer border-none bg-transparent p-0 text-m-t4 hover:text-m-t2"
              data-testid="memo-archive-search-clear"
            >
              <X size={12} aria-hidden />
            </button>
          ) : null}
        </div>

        {archived.isPending ? (
          <p className="m-0 text-[12px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
            <T id="memo.loading" />
          </p>
        ) : items.length === 0 ? (
          <div
            className="rounded-npc-card border border-dashed border-m-line2 bg-m-bg1 px-[18px] py-[22px] text-center"
            data-testid="memo-archive-empty"
          >
            <h2 className="m-0 text-[19px] font-medium text-m-t1"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              <T id="memo.archive.empty.title" />
            </h2>
            <p className="mx-auto mb-0 mt-[6px] max-w-[520px] text-[15px] leading-[1.6] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
              <T id="memo.archive.empty" />
            </p>
          </div>
        ) : (
          <ul
            className="m-0 list-none overflow-hidden rounded-npc-card border border-m-line2 bg-m-bg1 p-0"
            data-testid="memo-archive-list"
          >
            {items.map((memo) => {
              const projectName = memo.project_id
                ? (projects.data?.items.find((p) => p.id === memo.project_id)?.name ?? '')
                : ''
              return (
                <li
                  key={memo.id}
                  className="m-0 flex items-start gap-[12px] border-b border-m-line1 px-[13px] py-[12px] last:border-b-0"
                  data-testid="memo-archive-item"
                >
                  <div className="min-w-0 flex-1">
                    <p className="m-0 line-clamp-2 text-[18px] leading-[1.7] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                      {memo.has_password
                        ? lockedPlaceholder()
                        : memo.body || t('memo.list.voice.no_text', { duration: '—' })}
                    </p>
                    <div className="mt-[5px] flex flex-wrap items-center gap-[8px]">
                      <ScopeLabel
                        kind={memo.project_id ? { project: projectName } : 'misc'}
                      />
                      <span className="text-[11px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
                        {t('memo.archive.archived.at', {
                          date: whenShort(memo.archived_at ?? memo.updated_at ?? memo.created_at),
                        })}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => restore.mutate(memo.id)}
                    disabled={restore.isPending}
                    data-testid="memo-archive-restore"
                    className="h-[28px] shrink-0 cursor-pointer rounded-[7px] border border-m-line2 bg-m-bg1 px-[12px] font-[inherit] text-[12px] text-m-t2 hover:text-m-t1 disabled:opacity-50"  /* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */
                  >
                    <T id="memo.archive.restore" />
                  </button>
                </li>
              )
            })}
          </ul>
        )}

        <p className="mb-0 mt-[16px] text-[11.5px] text-m-t3"> {/* npc-token-allow: memo 设计稿 SPEC-tokens/SPEC-components 原值，整站 token 无此档 */}
          <T id="memo.archive.delete.note" />
          <span className="mx-[4px]">——</span>
          <T id="memo.archive.delete.note.body" />
        </p>
        </div>
      </div>
    </div>
  )
}
